import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState } from 'react';
import CustomSelect2 from './CustomSelect2';
import { Box, Typography, FormControlLabel, Checkbox, Grid, TextField, InputAdornment, Select, MenuItem, colors, Button, Tab } from '@mui/material';
import SelectPlanCheckbox from './SelectPlanCheckbox';
import { ConfigContext } from '../utils/ConfigContext';
import { AuthContext } from '../utils/AuthContext';
import TableRatesRow from './TableRatesRow';
import { getQuotation } from '../services/CompanyQuotationService';
import getSafe from '../utils/getSafe';
import domtoimage from 'dom-to-image';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { onAdditionalInputChange } from '../utils/additionalInputsHandler';
import NotesDisplay from './NotesDisplay';
import TableCellText from './TableCellText';
import _ from 'lodash';
import TableCellRate from './TableCellRate';
import CopyImageButton from './CopyImageButton';
import PivotTableChartIcon from '@mui/icons-material/PivotTableChart';

const Company_Rates = forwardRef(({ appConfig, sx, clients, loadingStatus, currency, selectedPlans, setSelectedPlans,
  response, deductibles, setDeductibles, addToPendingTasks,
  discounts, setDiscounts, computedRates, setComputedRates,
  specificAreaOfCoverage, setSpecificAreaOfCoverage, areaOfCoverage,
  setShowImageCopy, selectedOptions, addSelectedOption,
  setViewSelectedOption, paymentFrequency, specificCurrency, setSpecificCurrency,
  specificPaymentFrequency, setSpecificPaymentFrequency,
  companySpecific, setCompanySpecific, loadings, setLoadings }, ref) => {
  // const { appConfig } = useContext(ConfigContext);

  const id = 'company';

  const companyDiscounts = getSafe(() => appConfig.content.specifics.company.company_discounts);
  const groupSizes = getSafe(() => appConfig.content.specifics.company.group_sizes);
  const MHD_Loadings = getSafe(() => appConfig.content.specifics.company.mhd_loadings);

  const [companyDiscount, setCompanyDiscount] = useState(getCompanyDiscountsLabel()[0]);
  const [groupSize, setGroupSize] = useState(getGroupSizesLabel()[0]);
  const [MHD_Loading, set_MHD_Loading] = useState(get_MHD_LoadingsLabel()[0]);


  const company_Plans = ['company_essential', 'company_classic', 'company_gold', 'company_classic_with_dental', 'company_gold_with_dental', 'company_gold_superior', 'company_gold_superior_with_dental'];

  const specific_area_of_coverages = getSafe(() => appConfig.content.set2_area_of_coverage);

  const currencies = getSafe(() => appConfig.content.currencies, []);

  const [assistanceCovers, setAssistanceCovers] = useState({
    evacuation: false,
    repatriation: false
  });

  const [transposeTable, setTransposeTable] = useState(true);

  useEffect(() => {
    computedRates[id] = getComputedRates();
    setComputedRates(computedRates);
  }, [response, discounts, loadings, companyDiscount, groupSize, MHD_Loading, paymentFrequency]);

  useEffect(() => {
    setTimeout(() => {
      addSelectedOption(id, 'company_discount', companyDiscount);
      addSelectedOption(id, 'group_size', groupSize);
      addSelectedOption(id, 'mhd_loading', MHD_Loading);
      addSelectedOption(id, 'specific_area_of_coverage', specificAreaOfCoverage[id]);
      addSelectedOption(id, 'specific_currency', specificCurrency[id]);
      addSelectedOption(id, 'specific_payment_frequency', specificPaymentFrequency[id]);
      addSelectedOption(id, 'deductible', deductibles[id]['label']);
      addSelectedOption(id, 'discount', discounts[id] == '' ? 0 : discounts[id]);
      addSelectedOption(id, 'loading', loadings[id] == '' ? 0 : loadings[id]);
      addSelectedOption(id, 'evacuation', assistanceCovers.evacuation);
      addSelectedOption(id, 'repatriation', assistanceCovers.repatriation);
    }, 1000);
  }, [assistanceCovers.evacuation, assistanceCovers.repatriation, companyDiscount, groupSize, MHD_Loading, specificAreaOfCoverage[id], specificCurrency[id], specificPaymentFrequency[id], deductibles[id]['label'], discounts[id], loadings[id]]);

  useEffect(() => {
    setViewSelectedOption(id, 'company_discount', setCompanyDiscount);
    setViewSelectedOption(id, 'group_size', setGroupSize);
    setViewSelectedOption(id, 'mhd_loading', set_MHD_Loading);

    setViewSelectedOption(id, 'company_discount', (option_value) => {
      companySpecific['company_discount'] = option_value;
      setCompanySpecific({ ...companySpecific });
    });

    setViewSelectedOption(id, 'group_size', (option_value) => {
      companySpecific['group_size'] = option_value;
      setCompanySpecific({ ...companySpecific });
    });

    setViewSelectedOption(id, 'mhd_loading', (option_value) => {
      companySpecific['mhd_loading'] = option_value;
      setCompanySpecific({ ...companySpecific });
    });

    setViewSelectedOption(id, 'deductible', (option_value) => {
      deductibles[id]['label'] = option_value;
      setDeductibles(deductibles);
    });
    setViewSelectedOption(id, 'discount', (option_value) => {
      discounts[id] = option_value;
      setDiscounts(discounts);
    });

    setViewSelectedOption(id, 'loading', (option_value) => {
      loadings[id] = option_value;
      setLoadings(loadings);
    });

    setViewSelectedOption(id, 'specific_area_of_coverage', (option_value) => {
      specificAreaOfCoverage[id] = option_value;
      setSpecificAreaOfCoverage(specificAreaOfCoverage);
    });

    setViewSelectedOption(id, 'specific_currency', (option_value) => {
      specificCurrency[id] = option_value;
      setSpecificCurrency(specificCurrency);
    });

    setViewSelectedOption(id, 'specific_payment_frequency', (option_value) => {
      specificPaymentFrequency[id] = option_value;
      setSpecificPaymentFrequency(specificPaymentFrequency);
    });

    setViewSelectedOption(id, 'evacuation', (option_value) => {
      assistanceCovers.evacuation = option_value == 1;
      setAssistanceCovers(assistanceCovers);
    });

    setViewSelectedOption(id, 'repatriation', (option_value) => {
      assistanceCovers.repatriation = option_value == 1;
      setAssistanceCovers(assistanceCovers);
    });
  }, [selectedOptions]);

  function getComputedRates() {
    if (!response) return;

    const rates = response.rates;
    const assistance_covers = response.assistance_covers;
    const discount = parseFloat(discounts[id]) || 0;
    const loading = parseFloat(loadings[id]) || 0;
    const totalPercent = ((100 - discount + loading) * 0.01);

    const computedRates = [];

    for (let i = 0; i < rates.length; i++) {
      computedRates.push({
        'client': rates[i].client,
        'currency': rates[i].currency,
        'essential': computedRate('essential', rates[i].essential) * totalPercent,
        'classic': computedRate('classic', rates[i].classic) * totalPercent,
        'gold': computedRate('gold', rates[i].gold) * totalPercent,
        'classic_with_dental': computedRate('classic_with_dental', rates[i].classic_with_dental) * totalPercent,
        'gold_with_dental': computedRate('gold_with_dental', rates[i].gold_with_dental) * totalPercent,
        'gold_superior': computedRate('gold_superior', rates[i].gold_superior) * totalPercent,
        'gold_superior_with_dental': computedRate('gold_superior_with_dental', rates[i].gold_superior_with_dental) * totalPercent,
        'evacuation': assistance_covers[i].evacuation,
        'repatriation': assistance_covers[i].repatriation
      });
    }

    return computedRates;
  }

  const getComputedRatesMemo = _.memoize(getComputedRates);


  function checkIfThereIsSelected_Company_Plan() {
    for (let i = 0; i < selectedPlans.length; i++) {
      if (company_Plans.includes(selectedPlans[i])) {
        return true;
      }
    }

    return false;
  }

  function getCompanyDiscountsLabel() {
    const labels = [];

    for (let companyDiscount in companyDiscounts) {
      labels.push(companyDiscount);
    }

    return labels;
  }

  function getGroupSizesLabel() {
    const labels = [];

    for (let groupSize in groupSizes) {
      labels.push(groupSize);
    }

    return labels;
  }

  function get_MHD_LoadingsLabel() {
    const labels = [];

    for (let MHD_Loading in MHD_Loadings) {
      labels.push(MHD_Loading);
    }

    return labels;
  }

  function getDeductibleIndex(deductible) {
    const deductibles = getSafe(() => appConfig.content.deductibles.company[specificCurrency[id]]);

    for (let i = 0; i < deductibles.length; i++) {
      if (deductible === deductibles[i]) {
        return 'D' + i;
      }
    }

    return 'D0';
  }

  function computeCompanyRates(rates) {
    const computedRates = [];

    for (let i = 0; i < rates.length; i++) {
      computedRates.push({
        'client': rates[i].client,
        'currency': rates[i].currency,
        'essential': computedRate('essential', rates[i].essential),
        'classic': computedRate('classic', rates[i].classic),
        'gold': computedRate('gold', rates[i].gold),
        'classic_with_dental': computedRate('classic_with_dental', rates[i].classic_with_dental),
        'gold_with_dental': computedRate('gold_with_dental', rates[i].gold_with_dental),
        'gold_superior': computedRate('gold_superior', rates[i].gold_superior),
        'gold_superior_with_dental': computedRate('gold_superior_with_dental', rates[i].gold_superior_with_dental),
      })
    }

    return computedRates;
  }

  function computedRate(plan, rate) {
    if (rate === 'Unavailable' || typeof rate === 'string') {
      return rate;
    }

    const MHD_LoadingValue = MHD_Loadings[MHD_Loading];
    const companyDiscountValue = companyDiscounts[companyDiscount][plan];
    const groupSizeValue = groupSizes[groupSize];

    let total = rate;

    // MHD loadings
    //total = total * (1 + MHD_LoadingValue);

    // company discount
    //total = total * (1 - companyDiscountValue);

    // group size discount
    //total = total * (1 - groupSizeValue);

    // const payment_frequency_divided = getPaymentFrequencyDivided();

    // total /= payment_frequency_divided;


    return total;
  }

  function getPaymentFrequencyDivided() {
    if (paymentFrequency === 'Annually') {
      return 1;
    } else if (paymentFrequency === 'Semi Annually') {
      return 2;
    } else if (paymentFrequency === 'Quarterly') {
      return 4;
    } else if (paymentFrequency === 'Monthly') {
      return 12;
    }
  }

  function getCompanyDiscountLabel(plan) {
    const val = companyDiscounts[companyDiscount][plan];

    return (val * 100).toFixed(0) + '%'
  }

  function onInputDiscountChange(e) {
    let value = e.target.value;

    // Allow only numbers and at most one decimal point
    value = value.replace(/[^0-9.]/g, '');

    // Prevent multiple decimals
    if ((value.match(/\./g) || []).length > 1) {
      value = value.substring(0, value.lastIndexOf('.'));
    }

    // Ensure only two decimal places
    if (value.includes('.')) {
      const [integerPart, decimalPart] = value.split('.');
      value = `${integerPart}.${decimalPart.substring(0, 2)}`;
    }

    // Remove leading zeros (but allow "0" or "0.x" scenarios)
    if (value !== '' && value !== '0' && value.startsWith('0') && !value.startsWith('0.')) {
      value = value.replace(/^0+/, '');
    }

    // Limit value to a maximum of 100
    if (parseFloat(value) > 100) {
      value = '100';
    }

    discounts[id] = value;

    setDiscounts({ ...discounts });
  }

  function getCompanyDeductibleIndex(deductible) {
    const deductibles = appConfig.content.deductibles.company[specificCurrency[id]];

    for (let i = 0; i < deductibles.length; i++) {
      if (deductible === deductibles[i]) {
        return 'D' + i;
      }
    }

    return 'D0';
  }

  function getSubtotalRate(plan) {
    const computedRates = getComputedRatesMemo();

    if (!computedRates) {
      return;
    }

    if (specificCurrency[id] !== computedRates?.[0]?.currency) {
      return '-';
    }

    let subTotal = 0;

    for (let i = 0; i < clients.length; i++) {
      const rate = computedRates?.[i]?.[plan];

      if (isNaN(rate)) {
        continue;
      }

      subTotal += rate;
    }

    return subTotal;
  }

  function getRate(clientIndex, plan) {
    const computedRates = getComputedRatesMemo();

    if (!computedRates) {
      return;
    }

    if (specificCurrency[id] !== computedRates?.[clientIndex]?.currency) {
      return '-';
    }

    return computedRates?.[clientIndex]?.[plan];
  }

  function getCurrency() {
    const computedRates = getComputedRatesMemo();

    if (!computedRates) {
      return;
    }

    if (computedRates.length > 0) {
      return computedRates[0].currency;
    }

    return 'USD';
  }

  const tableRowRef = useRef(null);
  const [maxHeight, setMaxHeight] = useState(0);

  useEffect(() => {
    if (tableRowRef.current === null) { return; }


    const parentTable = tableRowRef.current.closest('table');

    if (parentTable) {
      // Select all TableRow elements inside the TableBody
      const rows = document.querySelectorAll('#' + parentTable.id + ' .MuiTableBody-root .MuiTableRow-root');

      // Calculate the maximum height
      let max = 0;
      rows.forEach((row, idx) => {
        if (idx !== 0) {
          const height = row.offsetHeight;
          if (height > max) {
            max = height;
          }
        }

      });

      setMaxHeight(max);
    }
  }, [response]);

  return (
    <Box sx={sx}>
      <Paper sx={{ borderColor: '#e0e0e0', borderWidth: 2, pb: 2 }} variant='outlined' >
        <Typography variant='h5' sx={{ color: '#fff', background: '#00bcd4', px: 2, py: 1 }}>BUPA Company</Typography>

        <Box sx={{ p: 2 }}>
          {/* <Grid container alignItems='center'>
            <Grid item xs={1.5}>
              <Typography fontSize={18} fontWeight={600}>Select Plans</Typography>
            </Grid>
            <Grid item xs={1.2}>
              <FormControlLabel control={<SelectPlanCheckbox selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId='company_essential' />} label="Essential" />
            </Grid>
            <Grid item xs={1.2}>
              <FormControlLabel control={<SelectPlanCheckbox selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId='company_classic' />} label="Classic" />
            </Grid>
            <Grid item xs={1}>
              <FormControlLabel control={<SelectPlanCheckbox selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId='company_gold' />} label="Gold" />
            </Grid>
            <Grid item xs={1.3}>
              <FormControlLabel control={<SelectPlanCheckbox selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId='company_classic_with_dental' />} label="Classic with Dental" />
            </Grid>

            <Grid item xs={1.3}>
              <FormControlLabel control={<SelectPlanCheckbox selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId='company_gold_with_dental' />} label="Gold with Dental" />
            </Grid>
            <Grid item xs={1.3}>
              <FormControlLabel control={<SelectPlanCheckbox selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId='company_gold_superior' />} label="Gold Superior" />
            </Grid>
            <Grid item xs={1.3}>
              <FormControlLabel control={<SelectPlanCheckbox selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId='company_gold_superior_with_dental' />} label="Gold Superior with Dental" />
            </Grid>
            <Grid item xs={12}>
            </Grid>
            <Grid item xs={1.5}>
              <Typography fontSize={18} fontWeight={600}>Optional</Typography>
            </Grid>
            <Grid item xs={1.2}>
              <FormControlLabel control={<Checkbox checked={assistanceCovers.evacuation}
                onChange={(e) => { setAssistanceCovers({ ...assistanceCovers, evacuation: e.target.checked }) }} />}
                label="Evacuation" />
            </Grid>
            <Grid item xs={1.2}>
              <FormControlLabel control={<Checkbox checked={assistanceCovers.repatriation}
                onChange={(e) => { setAssistanceCovers({ ...assistanceCovers, repatriation: e.target.checked }) }} />}
                label="Repatriation" />
            </Grid>
            <Grid item xs={12}>
            </Grid>
            <Grid item xs={1.5}>
              <Typography fontSize={18} fontWeight={600}>Deductible</Typography>
            </Grid>
            <Grid item xs={1.7}>
              <CustomSelect2
                value={deductibles[id].label}
                onChange={(e) => {
                  // setDeductible(e.target.value);

                  // setDeductibleIndex(getDeductibleIndex(e.target.value));

                  const val = e.target.value;

                  const _deductibles = JSON.parse(JSON.stringify(deductibles));

                  _deductibles[id].label = val;
                  _deductibles[id].deductible_index = getDeductibleIndex(val);

                  setDeductibles(_deductibles);

                  addToPendingTasks([id]);

                  // handleUpdateLoadingStatus(true);
                  // setLoading(true);
                  // setTimeout(() => {
                  //   ref.current.handleGetQuotation();
                  // }, 200);

                }}
                items={getSafe(() => appConfig.content.deductibles.company[currency], [])}
                disabled={loadingStatus[id]}
              />
            </Grid>
            <Grid item xs={1}>
              <Typography fontSize={18} fontWeight={600}>Company Discount</Typography>
            </Grid>
            <Grid item xs={1.6}>
              <CustomSelect2
                value={companyDiscount}
                onChange={(e) => {
                  setCompanyDiscount(e.target.value);
                }}
                items={getCompanyDiscountsLabel()}
                disabled={loadingStatus[id]}
              />
            </Grid>
            <Grid item xs={1}>
              <Typography fontSize={18} fontWeight={600}>Group Size</Typography>
            </Grid>
            <Grid item xs={1}>
              <CustomSelect2
                value={groupSize}
                onChange={(e) => {
                  setGroupSize(e.target.value);
                }}
                items={getGroupSizesLabel()}
                disabled={loadingStatus[id]}
              />
            </Grid>
            <Grid item xs={1.2}>
              <Typography fontSize={18} fontWeight={600}>MHD Loading</Typography>
            </Grid>
            <Grid item xs={1}>
              <CustomSelect2
                value={MHD_Loading}
                onChange={(e) => {
                  set_MHD_Loading(e.target.value);
                }}
                items={get_MHD_LoadingsLabel()}
                disabled={loadingStatus[id]}
              />
            </Grid>

            <Grid item xs={2}>
              <Typography fontSize={18}>
                MHD Loading = <span display='inline-block' style={{ fontSize: 18, color: '#00bcd4', fontWeight: 600 }}>{MHD_Loadings[MHD_Loading] * 100}%</span>
              </Typography>
            </Grid>
            <Grid item xs={1.5}><Typography fontSize={18} fontWeight={600}>Area Of Coverage</Typography></Grid>
            <Grid item xs={2}>
              <Select
                size='small'
                value={specificAreaOfCoverage[id]}
                onChange={(e) => {
                  specificAreaOfCoverage[id] = e.target.value;
                  setSpecificAreaOfCoverage({ ...specificAreaOfCoverage });

                  addToPendingTasks([id]);
                }}
                sx={{ fontSize: 12, height: 40, width: '190px' }}
                fullWidth
                disabled={loadingStatus[id]}
              >
                <MenuItem value={'parent'} sx={{ color: colors.blue[500] }}>{areaOfCoverage}</MenuItem>
                {
                  specific_area_of_coverages && specific_area_of_coverages.map(area_of_coverage => (
                    <MenuItem sx={{ display: areaOfCoverage === area_of_coverage ? 'none' : 'block' }} key={area_of_coverage} value={area_of_coverage}>{area_of_coverage}</MenuItem>
                  ))
                }

              </Select>
            </Grid>
            <Grid item xs={1.5}><Typography fontSize={18} fontWeight={600}>Renewal/Further Discount</Typography></Grid>
            <Grid item xs={1}>
              <TextField
                size='small'
                sx={{ width: '80px', textAlign: 'end' }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                value={discounts[id]}
                onChange={onInputDiscountChange}
              />
            </Grid>
          </Grid> */}

          <Box sx={{ display: 'flex', gap: 5 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography fontSize={18} fontWeight={600}>Plan Type</Typography>
              <FormControlLabel control={<SelectPlanCheckbox selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId='company_essential' />} label="Essential" />
              <FormControlLabel control={<SelectPlanCheckbox selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId='company_classic' />} label="Classic" />
              <FormControlLabel control={<SelectPlanCheckbox selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId='company_classic_with_dental' />} label="Classic with Dental" />
              <FormControlLabel control={<SelectPlanCheckbox selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId='company_gold' />} label="Gold" />
              <FormControlLabel control={<SelectPlanCheckbox selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId='company_gold_with_dental' />} label="Gold with Dental" />
              <FormControlLabel control={<SelectPlanCheckbox selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId='company_gold_superior' />} label="Gold Superior" />
              <FormControlLabel control={<SelectPlanCheckbox selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId='company_gold_superior_with_dental' />} label="Gold Superior with Dental" />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography fontSize={18} fontWeight={600}>Assistance Cover</Typography>
              <FormControlLabel control={<Checkbox checked={assistanceCovers.evacuation}
                onChange={(e) => { setAssistanceCovers({ ...assistanceCovers, evacuation: e.target.checked }) }} />}
                label="Evacuation" />
              <FormControlLabel control={<Checkbox checked={assistanceCovers.repatriation}
                onChange={(e) => { setAssistanceCovers({ ...assistanceCovers, repatriation: e.target.checked }) }} />}
                label="Repatriation" />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'start' }}>
                <Typography fontSize={18} fontWeight={600} sx={{ width: 200 }}>Deductible Option</Typography>
                <CustomSelect2
                  sx={{ minWidth: 190 }}
                  value={deductibles[id].label}
                  onChange={(e) => {
                    // setDeductible(e.target.value);

                    // setDeductibleIndex(getDeductibleIndex(e.target.value));

                    const val = e.target.value;

                    const _deductibles = JSON.parse(JSON.stringify(deductibles));

                    _deductibles[id].label = val;
                    _deductibles[id].deductible_index = getDeductibleIndex(val)

                    setDeductibles(_deductibles);

                    addToPendingTasks([id]);

                    // handleUpdateLoadingStatus(true);
                    // setLoading(true);
                    // setTimeout(() => {
                    //   ref.current.handleGetQuotation();
                    // }, 200);

                  }}
                  items={getSafe(() => appConfig.content.deductibles.company[specificCurrency[id]], [])}
                  disabled={loadingStatus[id]}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'start' }}>
                <Typography fontSize={18} fontWeight={600} sx={{ width: 200 }}>Area of Coverage</Typography>
                <Select
                  size='small'
                  value={specificAreaOfCoverage[id]}
                  onChange={(e) => {
                    specificAreaOfCoverage[id] = e.target.value;
                    setSpecificAreaOfCoverage({ ...specificAreaOfCoverage });

                    addToPendingTasks([id]);
                  }}
                  sx={{ fontSize: 12, height: 40, width: '190px' }}
                  fullWidth
                  disabled={loadingStatus[id]}
                >
                  {
                    specific_area_of_coverages && specific_area_of_coverages.map(area_of_coverage => (
                      <MenuItem key={area_of_coverage} value={area_of_coverage}>{area_of_coverage}</MenuItem>
                    ))
                  }

                </Select>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'start' }}>
                <Typography fontSize={18} fontWeight={600} sx={{ width: 200 }}>Currency</Typography>
                <Select
                  size='small'
                  value={specificCurrency[id]}
                  onChange={(e) => {
                    specificCurrency[id] = e.target.value;
                    setSpecificCurrency({ ...specificCurrency });



                    // clearResponse();

                    // setDeductibles(prev => {
                    //   return { 
                    //     ...prev, 
                    //     [id]: {
                    //       label: getSafe(() => appConfig.content.deductibles.company[specificCurrency[id]][0]),
                    //       deductible_index: 'D0'
                    //     } 
                    //   };
                    // });


                    addToPendingTasks([id]);
                  }}
                  sx={{ fontSize: 12, height: 40, width: '190px', minWidth: 190 }}
                  fullWidth
                  disabled={loadingStatus[id]}
                >
                  {
                    currencies && currencies.map(currency => (
                      <MenuItem key={currency} value={currency}>{currency}</MenuItem>
                    ))
                  }

                </Select>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'start' }}>
                <Typography fontSize={18} fontWeight={600} sx={{ width: 200 }}>Payment Frequency</Typography>
                <Select
                  size='small'
                  value={specificPaymentFrequency[id]}
                  onChange={(e) => {
                    specificPaymentFrequency[id] = e.target.value;
                    setSpecificPaymentFrequency({ ...specificPaymentFrequency });

                    addToPendingTasks([id]);
                  }}
                  sx={{ fontSize: 12, height: 40, width: '190px', minWidth: 190 }}
                  fullWidth
                  disabled={loadingStatus[id]}
                >
                  {
                    ["Annually", "Semi Annually", "Quarterly", "Monthly"].map(payment_frequency => (
                      <MenuItem key={payment_frequency} value={payment_frequency}>{payment_frequency}</MenuItem>
                    ))
                  }

                </Select>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'start' }}>
                <Typography fontSize={18} fontWeight={600} sx={{ width: 200 }}>Company Discount</Typography>
                <CustomSelect2
                  sx={{ minWidth: 190 }}
                  value={companyDiscount}
                  onChange={(e) => {
                    setCompanyDiscount(e.target.value);

                    companySpecific['company_discount'] = e.target.value;
                    setCompanySpecific(companySpecific);

                    addToPendingTasks([id]);
                  }}
                  items={getCompanyDiscountsLabel()}
                  disabled={loadingStatus[id]}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'start' }}>
                <Typography fontSize={18} fontWeight={600} sx={{ width: 200 }}>Group Size</Typography>
                <CustomSelect2
                  sx={{ minWidth: 190 }}
                  value={groupSize}
                  onChange={(e) => {
                    setGroupSize(e.target.value);

                    companySpecific['group_size'] = e.target.value;
                    setCompanySpecific(companySpecific);

                    addToPendingTasks([id]);
                  }}
                  items={getGroupSizesLabel()}
                  disabled={loadingStatus[id]}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'start' }}>
                <Typography fontSize={18} fontWeight={600} sx={{ width: 200 }}>MHD Loading</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CustomSelect2
                    sx={{ minWidth: 190 }}
                    value={MHD_Loading}
                    onChange={(e) => {
                      set_MHD_Loading(e.target.value);

                      companySpecific['mhd_loading'] = e.target.value;
                      setCompanySpecific(companySpecific);

                      addToPendingTasks([id]);
                    }}
                    items={get_MHD_LoadingsLabel()}
                    disabled={loadingStatus[id]}
                  />
                  <Typography fontSize={18}>
                    <span display='inline-block' style={{ fontSize: 18, color: '#00bcd4', fontWeight: 600 }}>{MHD_Loadings[MHD_Loading] * 100}%</span>
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'start' }}>
                <Typography fontSize={18} fontWeight={600} sx={{ width: 200 }}>Renewal/Further Discount</Typography>
                <TextField
                  size='small'
                  sx={{ width: '80px', textAlign: 'end' }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  value={discounts[id]}
                  onChange={(e) => {
                    onAdditionalInputChange(e, id, 'discounts', { setDiscounts, setLoadings, discounts, loadings });
                  }}
                  autoComplete="off"
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'start' }}>
                <Typography fontSize={18} fontWeight={600} sx={{ width: 200 }}>Renewal/Further Loading</Typography>
                <TextField
                  size='small'
                  sx={{ width: '80px', textAlign: 'end' }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  value={loadings[id]}
                  onChange={(e) => {
                    onAdditionalInputChange(e, id, 'loadings', { setDiscounts, setLoadings, discounts, loadings });
                  }}
                  autoComplete="off"
                />
              </Box>
            </Box>
          </Box>


          {
            transposeTable ?
              <div>
                <div style={{ display: 'flex', justifyContent: 'end', width: '100%', marginBottom: 2 }}>
                  <Button
                    size='small'
                    endIcon={<PivotTableChartIcon />}
                    sx={{ mr: 1 }}
                    onClick={() => {
                      setTransposeTable(false);
                    }}
                  >TRANSPOSE TABLE</Button>
                  <CopyImageButton
                    elementId='table-company-rates-transpose'
                    setShowImageCopy={setShowImageCopy}
                  />
                </div>
                <TableContainer component={Paper}>
                  <Table id='table-company-rates-transpose' size='small' sx={{ minWidth: 650, background: '#fff' }}>
                    <TableHead sx={{ background: '#00bcd4', color: '#fff' }}>
                      {
                        companyDiscount !== 'No Discount' &&
                        <TableRow>
                          <TableCell colSpan={3} sx={{ color: '#fff', fontWeight: 600, textAlign: 'right' }}>
                            Product Discount
                          </TableCell>
                          {
                            selectedPlans.includes('company_essential') &&
                            <TableCellText content={getCompanyDiscountLabel('essential')} sx={{ textAlign: 'center', color: '#fff', fontWeight: 600 }} />
                          }
                          {
                            selectedPlans.includes('company_classic') &&
                            <TableCellText content={getCompanyDiscountLabel('classic')} sx={{ textAlign: 'center', color: '#fff', fontWeight: 600 }} />
                          }
                          {
                            selectedPlans.includes('company_classic_with_dental') &&
                            <TableCellText content={getCompanyDiscountLabel('classic_with_dental')} sx={{ textAlign: 'center', color: '#fff', fontWeight: 600 }} />
                          }
                          {
                            selectedPlans.includes('company_gold') &&
                            <TableCellText content={getCompanyDiscountLabel('gold')} sx={{ textAlign: 'center', color: '#fff', fontWeight: 600 }} />
                          }
                          {
                            selectedPlans.includes('company_gold_with_dental') &&
                            <TableCellText content={getCompanyDiscountLabel('gold_with_dental')} sx={{ textAlign: 'center', color: '#fff', fontWeight: 600 }} />
                          }
                          {
                            selectedPlans.includes('company_gold_superior') &&
                            <TableCellText content={getCompanyDiscountLabel('gold_superior')} sx={{ textAlign: 'center', color: '#fff', fontWeight: 600 }} />
                          }
                          {
                            selectedPlans.includes('company_gold_superior_with_dental') &&
                            <TableCellText content={getCompanyDiscountLabel('gold_superior_with_dental')} sx={{ textAlign: 'center', color: '#fff', fontWeight: 600 }} />
                          }
                          {
                            assistanceCovers.evacuation &&
                            <TableCellText content='' sx={{ textAlign: 'center', color: '#fff', fontWeight: 600, backgroundColor: '#002047' }} bgColor='#002047' />
                          }
                          {
                            assistanceCovers.repatriation &&
                            <TableCellText content='' sx={{ textAlign: 'center', color: '#fff', fontWeight: 600, backgroundColor: '#002047' }} bgColor='#002047' />
                          }
                        </TableRow>
                      }

                      <TableRow>
                        {/* <TableCell sx={{ width: 170, color: '#fff', fontWeight: 600 }}>Plan</TableCell>
                  <TableCell sx={{ width: 60, color: '#fff', fontWeight: 600 }}>Product Discount</TableCell>
                  {
                    clients.map((client, idx) => {
                      if (client.full_name.trim() === '') {
                        return <TableCell sx={{ width: 200, fontWeight: 600, color: '#fff' }} key={idx}>_</TableCell>
                      }

                      return <TableCell sx={{ width: 200, color: '#fff', fontWeight: 600 }} key={idx}><div>{client.full_name} ({client.age})</div><div>{client.country_of_residence}</div>  </TableCell>
                    })
                  }
                  <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Subtotal</TableCell> */}
                        <TableCell sx={{ width: 150, maxWidth: 150, color: '#fff', fontWeight: 600, height: 57 }}>Name</TableCell>
                        <TableCell sx={{ width: 70, maxWidth: 70, color: '#fff', fontWeight: 600 }}>Age</TableCell>
                        <TableCell sx={{ width: 150, maxWidth: 150, color: '#fff', fontWeight: 600 }}>Country</TableCell>
                        {
                          selectedPlans.includes('company_essential') &&
                          <TableCell sx={{ maxWidth: 100, color: '#fff', fontWeight: 600, backgroundColor: '#2e7d32' }}>Essential</TableCell>
                        }

                        {
                          selectedPlans.includes('company_classic') &&
                          <TableCell sx={{ maxWidth: 100, color: '#fff', fontWeight: 600, backgroundColor: '#616161' }}>Classic</TableCell>
                        }

                        {
                          selectedPlans.includes('company_classic_with_dental') &&
                          <TableCell sx={{ maxWidth: 100, color: '#fff', fontWeight: 600, backgroundColor: '#616161' }}>Classic with Dental</TableCell>
                        }

                        {
                          selectedPlans.includes('company_gold') &&
                          <TableCell sx={{ maxWidth: 100, color: '#fff', fontWeight: 600, backgroundColor: '#ff5722' }}>Gold</TableCell>
                        }

                        {
                          selectedPlans.includes('company_gold_with_dental') &&
                          <TableCell sx={{ maxWidth: 100, color: '#fff', fontWeight: 600, backgroundColor: '#ff5722' }}>Gold with Dental</TableCell>
                        }

                        {
                          selectedPlans.includes('company_gold_superior') &&
                          <TableCell sx={{ maxWidth: 100, color: '#fff', fontWeight: 600, backgroundColor: '#e64a19' }}>Gold Superior</TableCell>
                        }

                        {
                          selectedPlans.includes('company_gold_superior_with_dental') &&
                          <TableCell sx={{ maxWidth: 100, color: '#fff', fontWeight: 600, backgroundColor: '#e64a19' }}>Gold Superior with Dental</TableCell>
                        }

                        {
                          assistanceCovers.evacuation &&
                          <TableCell sx={{ maxWidth: 100, color: '#fff', fontWeight: 600, backgroundColor: '#002047' }}>Evacuation</TableCell>
                        }

                        {
                          assistanceCovers.repatriation &&
                          <TableCell sx={{ maxWidth: 100, color: '#fff', fontWeight: 600, backgroundColor: '#002047' }}>Repatriation</TableCell>
                        }


                      </TableRow>

                    </TableHead>
                    <TableBody>
                      {
                        clients.map((client, idx) => {
                          return (
                            <TableRow key={client.uid} ref={tableRowRef} sx={{ height: maxHeight }}>
                              <TableCellText sx={{ height: maxHeight }} content={client.full_name} />
                              <TableCellText content={client.age} />
                              <TableCellText content={client.country_of_residence} />
                              {
                                selectedPlans.includes('company_essential') &&
                                <TableCellRate
                                  skeletonWidth={60}
                                  rate={getRate(idx, 'essential')}
                                  currency={getCurrency()}
                                  isLoading={loadingStatus[id]}
                                />
                              }
                              {
                                selectedPlans.includes('company_classic') &&
                                <TableCellRate
                                  skeletonWidth={60}
                                  rate={getRate(idx, 'classic')}
                                  currency={getCurrency()}
                                  isLoading={loadingStatus[id]}
                                />
                              }
                              {
                                selectedPlans.includes('company_classic_with_dental') &&
                                <TableCellRate
                                  skeletonWidth={60}
                                  rate={getRate(idx, 'classic_with_dental')}
                                  currency={getCurrency()}
                                  isLoading={loadingStatus[id]}
                                />
                              }
                              {
                                selectedPlans.includes('company_gold') &&
                                <TableCellRate
                                  skeletonWidth={60}
                                  rate={getRate(idx, 'gold')}
                                  currency={getCurrency()}
                                  isLoading={loadingStatus[id]}
                                />
                              }
                              {
                                selectedPlans.includes('company_gold_with_dental') &&
                                <TableCellRate
                                  skeletonWidth={60}
                                  rate={getRate(idx, 'gold_with_dental')}
                                  currency={getCurrency()}
                                  isLoading={loadingStatus[id]}
                                />
                              }
                              {
                                selectedPlans.includes('company_gold_superior') &&
                                <TableCellRate
                                  skeletonWidth={60}
                                  rate={getRate(idx, 'gold_superior')}
                                  currency={getCurrency()}
                                  isLoading={loadingStatus[id]}
                                />
                              }
                              {
                                selectedPlans.includes('company_gold_superior_with_dental') &&
                                <TableCellRate
                                  skeletonWidth={60}
                                  rate={getRate(idx, 'gold_superior_with_dental')}
                                  currency={getCurrency()}
                                  isLoading={loadingStatus[id]}
                                />
                              }
                              {
                                assistanceCovers.evacuation &&
                                <TableCellRate
                                  skeletonWidth={60}
                                  rate={getRate(idx, 'evacuation')}
                                  currency={getCurrency()}
                                  isLoading={loadingStatus[id]}
                                />
                              }
                              {
                                assistanceCovers.repatriation &&
                                <TableCellRate
                                  skeletonWidth={60}
                                  rate={getRate(idx, 'repatriation')}
                                  currency={getCurrency()}
                                  isLoading={loadingStatus[id]}
                                />
                              }
                            </TableRow>
                          );
                        })
                      }
                      <TableRow>
                        <TableCellText sx={{ height: maxHeight, textAlign: 'left', fontWeight: 600 }} content='Subtotal' colSpan={3} bgColor='#bdbdbd' />
                        {
                          selectedPlans.includes('company_essential') &&
                          <TableCellRate
                            skeletonWidth={60}
                            rate={getSubtotalRate('essential')}
                            currency={getCurrency()}
                            isLoading={loadingStatus[id]}
                            bgColor='#bdbdbd'
                          />
                        }
                        {
                          selectedPlans.includes('company_classic') &&
                          <TableCellRate
                            skeletonWidth={60}
                            rate={getSubtotalRate('classic')}
                            currency={getCurrency()}
                            isLoading={loadingStatus[id]}
                            bgColor='#bdbdbd'
                          />
                        }
                        {
                          selectedPlans.includes('company_classic_with_dental') &&
                          <TableCellRate
                            skeletonWidth={60}
                            rate={getSubtotalRate('classic_with_dental')}
                            currency={getCurrency()}
                            isLoading={loadingStatus[id]}
                            bgColor='#bdbdbd'
                          />
                        }
                        {
                          selectedPlans.includes('company_gold') &&
                          <TableCellRate
                            skeletonWidth={60}
                            rate={getSubtotalRate('gold')}
                            currency={getCurrency()}
                            isLoading={loadingStatus[id]}
                            bgColor='#bdbdbd'
                          />
                        }
                        {
                          selectedPlans.includes('company_gold_with_dental') &&
                          <TableCellRate
                            skeletonWidth={60}
                            rate={getSubtotalRate('gold_with_dental')}
                            currency={getCurrency()}
                            isLoading={loadingStatus[id]}
                            bgColor='#bdbdbd'
                          />
                        }
                        {
                          selectedPlans.includes('company_gold_superior') &&
                          <TableCellRate
                            skeletonWidth={60}
                            rate={getSubtotalRate('gold_superior')}
                            currency={getCurrency()}
                            isLoading={loadingStatus[id]}
                            bgColor='#bdbdbd'
                          />
                        }
                        {
                          selectedPlans.includes('company_gold_superior_with_dental') &&
                          <TableCellRate
                            skeletonWidth={60}
                            rate={getSubtotalRate('gold_superior_with_dental')}
                            currency={getCurrency()}
                            isLoading={loadingStatus[id]}
                            bgColor='#bdbdbd'
                          />
                        }
                        {
                          assistanceCovers.evacuation &&
                          <TableCellRate
                            skeletonWidth={60}
                            rate={getSubtotalRate('evacuation')}
                            currency={getCurrency()}
                            isLoading={loadingStatus[id]}
                            bgColor='#bdbdbd'
                          />
                        }
                        {
                          assistanceCovers.repatriation &&
                          <TableCellRate
                            skeletonWidth={60}
                            rate={getSubtotalRate('repatriation')}
                            currency={getCurrency()}
                            isLoading={loadingStatus[id]}
                            bgColor='#bdbdbd'
                          />
                        }
                      </TableRow>
                    </TableBody>
                  </Table>

                </TableContainer>
              </div> :
              (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'end', width: '100%', marginBottom: 2 }}>
                    <Button
                      size='small'
                      endIcon={<PivotTableChartIcon />}
                      sx={{ mr: 1 }}
                      onClick={() => {
                        setTransposeTable(true);
                      }}
                    >TRANSPOSE TABLE</Button>
                    <CopyImageButton
                      elementId='table-company-rates'
                      setShowImageCopy={setShowImageCopy}
                    />
                  </div>

                  <TableContainer component={Paper}>
                    <Table id='table-company-rates' size='small' sx={{ minWidth: 650, background: '#fff' }}>
                      <TableHead sx={{ background: '#00bcd4', color: '#fff' }}>
                        <TableRow>
                          <TableCell sx={{ width: 170, color: '#fff', fontWeight: 600 }}>Plan</TableCell>
                          <TableCell sx={{ width: 60, color: '#fff', fontWeight: 600 }}>Product Discount</TableCell>
                          {
                            clients.map((client, idx) => {
                              if (client.full_name.trim() === '') {
                                return <TableCell sx={{ width: 200, fontWeight: 600, color: '#fff' }} key={idx}>_</TableCell>
                              }

                              return <TableCell sx={{ width: 200, color: '#fff', fontWeight: 600 }} key={idx}><div>{client.full_name} ({client.age})</div><div>{client.country_of_residence}</div>  </TableCell>
                            })
                          }
                          <TableCell sx={{ color: '#fff', fontWeight: 600 }}>Subtotal</TableCell>
                        </TableRow>

                      </TableHead>
                      <TableBody>
                        <TableRatesRow
                          planId='company_essential'
                          planName='essential'
                          planLabel='Essential'
                          clients={clients}
                          selectedPlans={selectedPlans}
                          bgColor='#80deea'
                          loading={loadingStatus[id]}
                          rates={response ? computeCompanyRates(response.rates) : []}
                          currency={currency}
                          additionalColumns={[{ value: getCompanyDiscountLabel('essential'), textAlign: 'center', bgColor: '#4dd0e1' }]}
                          discount={discounts[id]}
                          additionalLoading={loadings[id]}
                          visible={selectedPlans.includes('company_essential')}
                        />
                        <TableRatesRow
                          planId='company_classic'
                          planName='classic'
                          planLabel='Classic'
                          clients={clients}
                          selectedPlans={selectedPlans}
                          bgColor='#80deea'
                          loading={loadingStatus[id]}
                          rates={response ? computeCompanyRates(response.rates) : []}
                          currency={currency}
                          additionalColumns={[{ value: getCompanyDiscountLabel('classic'), textAlign: 'center', bgColor: '#4dd0e1' }]}
                          discount={discounts[id]}
                          additionalLoading={loadings[id]}
                          visible={selectedPlans.includes('company_classic')}
                        />
                        <TableRatesRow
                          planId='company_classic_with_dental'
                          planName='classic_with_dental'
                          planLabel='Classic with Dental'
                          clients={clients}
                          selectedPlans={selectedPlans}
                          bgColor='#80deea'
                          loading={loadingStatus[id]}
                          rates={response ? computeCompanyRates(response.rates) : []}
                          currency={currency}
                          additionalColumns={[{ value: getCompanyDiscountLabel('classic_with_dental'), textAlign: 'center', bgColor: '#4dd0e1' }]}
                          discount={discounts[id]}
                          additionalLoading={loadings[id]}
                          visible={selectedPlans.includes('company_classic_with_dental')}
                        />
                        <TableRatesRow
                          planId='company_gold'
                          planName='gold'
                          planLabel='Gold'
                          clients={clients}
                          selectedPlans={selectedPlans}
                          bgColor='#80deea'
                          loading={loadingStatus[id]}
                          rates={response ? computeCompanyRates(response.rates) : []}
                          currency={currency}
                          additionalColumns={[{ value: getCompanyDiscountLabel('gold'), textAlign: 'center', bgColor: '#4dd0e1' }]}
                          discount={discounts[id]}
                          additionalLoading={loadings[id]}
                          visible={selectedPlans.includes('company_gold')}
                        />

                        <TableRatesRow
                          planId='company_gold_with_dental'
                          planName='gold_with_dental'
                          planLabel='Gold with Dental'
                          clients={clients}
                          selectedPlans={selectedPlans}
                          bgColor='#80deea'
                          loading={loadingStatus[id]}
                          rates={response ? computeCompanyRates(response.rates) : []}
                          currency={currency}
                          additionalColumns={[{ value: getCompanyDiscountLabel('gold_with_dental'), textAlign: 'center', bgColor: '#4dd0e1' }]}
                          discount={discounts[id]}
                          additionalLoading={loadings[id]}
                          visible={selectedPlans.includes('company_gold_with_dental')}
                        />
                        <TableRatesRow
                          planId='company_gold_superior'
                          planName='gold_superior'
                          planLabel='Gold Superior'
                          clients={clients}
                          selectedPlans={selectedPlans}
                          bgColor='#80deea'
                          loading={loadingStatus[id]}
                          rates={response ? computeCompanyRates(response.rates) : []}
                          currency={currency}
                          additionalColumns={[{ value: getCompanyDiscountLabel('gold_superior'), textAlign: 'center', bgColor: '#4dd0e1' }]}
                          discount={discounts[id]}
                          additionalLoading={loadings[id]}
                          visible={selectedPlans.includes('company_gold_superior')}
                        />
                        <TableRatesRow
                          planId='company_gold_superior_with_dental'
                          planName='gold_superior_with_dental'
                          planLabel='Gold Superior Dental'
                          clients={clients}
                          selectedPlans={selectedPlans}
                          bgColor='#80deea'
                          loading={loadingStatus[id]}
                          rates={response ? computeCompanyRates(response.rates) : []}
                          currency={currency}
                          additionalColumns={[{ value: getCompanyDiscountLabel('gold_superior_with_dental'), textAlign: 'center', bgColor: '#4dd0e1' }]}
                          discount={discounts[id]}
                          additionalLoading={loadings[id]}
                          visible={selectedPlans.includes('company_gold_superior_with_dental')}
                        />
                        {
                          (assistanceCovers.evacuation || assistanceCovers.repatriation) &&
                          <TableRow sx={{ backgroundColor: '#fff' }}>
                            <TableCell colSpan={clients.length + 3} sx={{ fontWeight: 600, backgroundColor: '#fff' }}>Optional</TableCell>
                          </TableRow>
                        }

                        {/* <TableRow>
                  <TableCell colSpan={clients.length + 3} sx={{ fontWeight: 600 }}>Optional</TableCell>
                </TableRow> */}

                        <TableRatesRow
                          planId='company_evacuation'
                          planName='evacuation'
                          planLabel='Evacuation'
                          clients={clients}
                          selectedPlans={selectedPlans}
                          bgColor='#eeeeee'
                          loading={loadingStatus[id]}
                          rates={response ? response.assistance_covers : []}
                          currency={currency}
                          planLabelColSpan={2}
                          visible={assistanceCovers.evacuation}
                        />

                        <TableRatesRow
                          planId='company_repatriation'
                          planName='repatriation'
                          planLabel='Repatriation'
                          clients={clients}
                          selectedPlans={selectedPlans}
                          bgColor='#eeeeee'
                          loading={loadingStatus[id]}
                          rates={response ? response.assistance_covers : []}
                          currency={currency}
                          planLabelColSpan={2}
                          visible={assistanceCovers.repatriation}
                        />
                        {/* {
                  checkIfThereIsSelected_Company_Plan() == false &&
                  <TableRow>
                    <TableCell>No Plan Selected</TableCell>
                  </TableRow>
                } */}
                      </TableBody>
                    </Table>

                  </TableContainer>
                </div>
              )
          }
          {/* TRANSPOSE */}



          <NotesDisplay appConfig={appConfig} insurerId={id} />
        </Box>


      </Paper>

    </Box>
  );
});

export default Company_Rates;